import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useStateContext} from "../contexts/ContextProvider";
import {useCartContext} from "../contexts/CartContext";
import {createItemsPaypalPayment, createItemsStripeCheckoutSession, createItemsMolliePayment} from "../api/apiClient";
import {toast} from "react-toastify";
import {isPayByBankEnabled, isMollieEnabled} from "../utility/featureFlags";
import {BOOKING_ERROR_MESSAGES, INVALID_COUPON_ERROR_CODE} from "../utility/bookingErrorMessages";
import {getCouponPaymentMethod} from "../utility/couponPaymentMethod";
import {toCheckoutItems, useItemsCheckoutPrice} from "../hooks/useItemsCheckoutPrice";
import CouponCodeField from "../components/CouponCodeField";
import ItemImage from "../components/ItemImage";

const OUT_OF_STOCK_MESSAGE = "Leider ist mindestens ein Artikel in deinem Warenkorb nicht mehr in der gewünschten Menge verfügbar. Bitte überprüfe deinen Warenkorb.";
const GENERIC_ERROR_MESSAGE = "Fehler bei der Zahlung. Bitte versuche es erneut.";
const COUPON_CHECK_FAILED_MESSAGE = "Dein Gutschein konnte gerade nicht geprüft werden. Bitte versuche es erneut.";

const ShopCheckoutPage = () => {
    const {token, user} = useStateContext();
    const {cartLines} = useCartContext();
    const navigate = useNavigate();

    // Preselect Pay by Bank, but only when its block is actually rendered - otherwise the buy
    // button would be enabled for a method the customer never got to see.
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
        () => (isMollieEnabled() || isPayByBankEnabled()) ? "paybybank" : null
    );
    const [coupon, setCoupon] = useState(null);
    const [reloadPrice, setReloadPrice] = useState(0);
    const {checkoutPrice, isPricing, pricingFailed} = useItemsCheckoutPrice(cartLines, coupon, getCouponPaymentMethod(selectedPaymentMethod), reloadPrice);
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        if (!token) {
            navigate("/login");
        }
    }, [navigate, token]);

    useEffect(() => {
        if (cartLines.length === 0) {
            navigate("/cart");
        }
    }, [cartLines.length, navigate]);

    if (!token || cartLines.length === 0) {
        return <div>Loading...</div>;
    }

    const currency = (checkoutPrice?.currency ?? cartLines[0].currency).toUpperCase();
    // Until the API has priced the cart, show the list price summed from the cart lines.
    const totalPrice = checkoutPrice?.totalPrice ?? cartLines.reduce((sum, line) => sum + line.bruttoPrice * line.quantity, 0);
    const payablePrice = checkoutPrice?.payablePrice ?? totalPrice;
    const couponCode = checkoutPrice?.coupon?.code ?? null;
    const items = toCheckoutItems(cartLines);
    const isButtonDisabled = !selectedPaymentMethod || !agreeTerms || processing || isPricing;

    const handleCheckoutResponse = (response) => {
        if (response === "item_out_of_stock") {
            toast.error(OUT_OF_STOCK_MESSAGE);
            setProcessing(false);
            return;
        }

        if (response?.status === "created" && (response.approvalUrl || response.url || response.linkUrl || response.checkoutUrl)) {
            window.location.href = response.approvalUrl || response.url || response.linkUrl || response.checkoutUrl;
            return;
        }

        toast.error(GENERIC_ERROR_MESSAGE);
        setProcessing(false);
    };

    const handlePaymentError = (error) => {
        console.error("Shop checkout error:", error);

        let errorData;
        try {
            errorData = JSON.parse(error.message);
        } catch (exception) {
            errorData = null;
        }

        if (errorData?.code === INVALID_COUPON_ERROR_CODE) {
            // Reprice the cart so the coupon error and the real price are shown.
            setReloadPrice((prev) => prev + 1);
            setProcessing(false);
            return;
        }

        toast.error(BOOKING_ERROR_MESSAGES[errorData?.code] || GENERIC_ERROR_MESSAGE);
        setProcessing(false);
    };

    const handlePayment = async () => {
        setProcessing(true);

        if (selectedPaymentMethod === "paypal") {
            try {
                const response = await createItemsPaypalPayment(items, couponCode);
                handleCheckoutResponse(response);
            } catch (error) {
                handlePaymentError(error);
            }
        } else if (selectedPaymentMethod === "creditcard") {
            createItemsStripeCheckoutSession(items, couponCode, "card")
                .then(handleCheckoutResponse)
                .catch(handlePaymentError);
        } else if (selectedPaymentMethod === "paybybank") {
            const payByBankRequest = isMollieEnabled()
                ? createItemsMolliePayment(items, couponCode)
                : createItemsStripeCheckoutSession(items, couponCode, "pay_by_bank");

            payByBankRequest
                .then(handleCheckoutResponse)
                .catch(handlePaymentError);
        } else if (selectedPaymentMethod === "sepa") {
            createItemsStripeCheckoutSession(items, couponCode, "sepa_debit")
                .then(handleCheckoutResponse)
                .catch(handlePaymentError);
        } else {
            setProcessing(false);
            toast.error("Bitte wähle eine Zahlungsmethode aus.");
        }
    };

    return (
        <div className="container mx-auto max-w-6xl p-6 bg-white rounded-lg shadow-md">
            <div className="mb-6">
                <button
                    className="text-pegasusPurple hover:text-pink-700 font-medium"
                    onClick={() => navigate("/cart")}
                >
                    &lt; Zurück zum Warenkorb
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="col-span-2">
                    <h2 className="text-lg font-bold text-gray-800 mb-4">Deine Daten</h2>
                    <p className="text-gray-700 mb-6">
                        Willkommen <span className="font-semibold">{user?.firstName || "Gast"}</span>
                    </p>

                    <h2 className="text-lg font-bold text-gray-800 mb-4">Kasse</h2>
                    <div className="space-y-4">
                        {(isMollieEnabled() || isPayByBankEnabled()) && (
                            <div
                                className={`border p-4 rounded-lg ${selectedPaymentMethod === "paybybank" ? "border-pegasusPurple" : "border-gray-300"}`}
                            >
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        className="mr-4"
                                        checked={selectedPaymentMethod === "paybybank"}
                                        onChange={() => setSelectedPaymentMethod("paybybank")}
                                    />
                                    <span>💚 Pay by Bank</span>
                                    <span className="ml-2 text-xs font-semibold text-pegasusPurple bg-pink-100 px-2 py-0.5 rounded-full">
                                        EMPFOHLEN
                                    </span>
                                </label>
                                <p className="mt-2 text-gray-600 text-sm">
                                    Direkt und sicher über deine Bank-App bezahlen – so unterstützt du unser Studio mit den geringsten Gebühren.
                                </p>
                                {isMollieEnabled() && (
                                    <p className="mt-1 text-gray-500 text-xs">
                                        In deiner Banking-App erscheint{" "}
                                        <span className="font-semibold text-gray-700">Stichting Mollie Payments</span>, unser Zahlungspartner.
                                    </p>
                                )}
                            </div>
                        )}
                        <div
                            className={`border p-4 rounded-lg ${selectedPaymentMethod === "sepa" ? "border-pegasusPurple" : "border-gray-300"}`}
                        >
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="radio"
                                    name="paymentMethod"
                                    className="mr-4"
                                    checked={selectedPaymentMethod === "sepa"}
                                    onChange={() => setSelectedPaymentMethod("sepa")}
                                />
                                <span>SEPA Lastschrift</span>
                            </label>
                        </div>
                        <div
                            className={`border p-4 rounded-lg ${selectedPaymentMethod === "creditcard" ? "border-pegasusPurple" : "border-gray-300"}`}
                        >
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="radio"
                                    name="paymentMethod"
                                    className="mr-4"
                                    checked={selectedPaymentMethod === "creditcard"}
                                    onChange={() => setSelectedPaymentMethod("creditcard")}
                                />
                                <span>Kreditkarte</span>
                            </label>
                        </div>
                        <div
                            className={`border p-4 rounded-lg ${selectedPaymentMethod === "paypal" ? "border-pegasusPurple" : "border-gray-300"}`}
                        >
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="radio"
                                    name="paymentMethod"
                                    className="mr-4"
                                    checked={selectedPaymentMethod === "paypal"}
                                    onChange={() => setSelectedPaymentMethod("paypal")}
                                />
                                <span>PayPal</span>
                            </label>
                        </div>
                    </div>
                </div>

                <div>
                    <h2 className="text-lg font-bold text-gray-800 mb-4">Deine Bestellung</h2>
                    <div className="border p-4 rounded-lg shadow-md space-y-3">
                        {cartLines.map((line) => (
                            <div key={line.itemVariantId} className="flex items-center justify-between text-sm gap-3">
                                <div className="flex items-center gap-3 min-w-0">
                                    <ItemImage src={line.itemImage} alt={line.itemName} className="w-10 h-10 rounded-md object-cover flex-shrink-0"/>
                                    <span className="truncate">
                                        {line.itemName} ({line.variantProperties.map((p) => p.value).join(" · ")}) × {line.quantity}
                                    </span>
                                </div>
                                <span className="whitespace-nowrap">{(line.bruttoPrice * line.quantity).toFixed(2)} {line.currency.toUpperCase()}</span>
                            </div>
                        ))}
                        <div className="flex justify-between text-sm gap-3">
                            <div>
                                <p>Lieferung: Abholung im Studio</p>
                                <p className="text-xs text-gray-500">Luxemburgerstraße 148, 54294 Trier</p>
                            </div>
                            <span className="whitespace-nowrap">kostenlos</span>
                        </div>
                        {checkoutPrice?.coupon && (
                            <div className="pt-2 border-t text-sm space-y-1">
                                <p>Zwischensumme: {totalPrice.toFixed(2)} {currency}</p>
                                <p className="text-green-700">
                                    Gutschein {checkoutPrice.coupon.name}: -{checkoutPrice.discountAmount.toFixed(2)} {currency}
                                </p>
                            </div>
                        )}
                        <p className="font-bold text-gray-800 pt-2 border-t">
                            Gesamt: {payablePrice.toFixed(2)} {currency}
                        </p>
                        <CouponCodeField
                            // While repricing, keep showing the coupon the customer applied rather than none.
                            activeCouponCode={isPricing ? coupon : couponCode}
                            errorMessage={coupon && pricingFailed ? COUPON_CHECK_FAILED_MESSAGE : checkoutPrice?.couponErrorMessage}
                            onApply={setCoupon}
                            onRemove={() => setCoupon(null)}
                        />
                    </div>

                    <div className="mt-6 space-y-2">
                        <label className="flex items-start space-x-2 text-sm">
                            <input
                                type="checkbox"
                                className="mt-1"
                                checked={agreeTerms}
                                onChange={() => setAgreeTerms(!agreeTerms)}
                            />
                            <span>
                                Ich akzeptiere die{" "}
                                <a href={`${process.env.REACT_APP_BASE_SITE_URL}/allgemeine-geschaeftsbedingungen`} className="text-pegasusPurple underline">AGB</a>{" "}
                                und die{" "}
                                <a href={`${process.env.REACT_APP_BASE_SITE_URL}/widerrufsbelehrung`} className="text-pegasusPurple underline">Widerrufsbelehrung</a>.
                            </span>
                        </label>
                        <p className="text-sm">
                            Informationen zur Verarbeitung deiner Daten findest du in unserer{" "}
                            <a href={`${process.env.REACT_APP_BASE_SITE_URL}/datenschutzerklaerung`} className="text-pegasusPurple underline">Datenschutzerklärung</a>.
                        </p>
                    </div>

                    <button
                        className={`w-full mt-4 py-2 rounded-lg font-semibold flex items-center justify-center ${
                            isButtonDisabled
                                ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                                : "bg-pegasusPurple text-white hover:bg-pegasusPurpleHover"
                        }`}
                        disabled={isButtonDisabled}
                        onClick={handlePayment}
                    >
                        {processing && (
                            <span className="inline-block w-4 h-4 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
                        )}
                        {processing ? "Wird verarbeitet..." : "JETZT ZAHLUNGSPFLICHTIG BESTELLEN"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ShopCheckoutPage;
